import type { Me, ModelScopesDto, PreparationState, Tenant } from '@bk/contracts';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { navigate } from '../../lib/router';
import { TenantContext, useTenant, useTenantQuery, type TenantContextValue } from '../../lib/useTenant';
import { Button, SectionLoader } from '../../ui';
import { ArchivePage } from './Archive';
import { CollectionPage } from './Collection';
import { etaText, Bar, useWork } from './WorkProgress';
import { ConnectPage } from './Connect';
import { ControlsPage, KpisPage } from './Controls';
import { EntityTypePage, NewEntityPage } from './DataModel';
import { DashboardPage, MemberDashboardPage } from './Dashboard';
import { RoleViewPage } from './RoleView';
import { EntityCardPage } from './EntityCard';
import { BuilderPage } from './Builder';
import { People } from './People';
import { Proposals } from './Proposals';
import { ProceduresPage } from './Procedures';
import { SitePage, SitesPage } from './Sites';
import { AccountsPage, CompanyPage, GovernancePage } from './Company';
import { RolesPage } from './Roles';
import { NewProcess, Processes } from './Processes';
import { ProcessDetailPage } from './ProcessDetail';
import { RunDetailPage, Runs } from './Runs';
import { Sidebar } from './Sidebar';
import { LevelChooserPage, LevelSwitch, readLevel, writeLevel } from './Livelli';
import { SettingsPage } from '../SettingsPage';
import { TopBar } from '../TopBar';
import { rememberTenant } from '../../lib/last-tenant';
import { useBrand } from '../../lib/brand';
import { useLevels } from './Livelli';
import { SoftwarePage } from './Software';
import { SoftwareDetailPage } from './SoftwareDetail';
import { StoredDataPage } from './StoredData';
import { Today } from './Today';
import { SystemsPage } from './Systems';
import { DataAccessPage } from './DataAccess';
import { MyDataPage } from './MyData';
import { ViewsPage } from './Views';
import { ScadenzarioPage } from './Scadenzario';
import { ExecutionsPage } from './Executions';
import { ConditionAnswersSync } from './ConditionAnswersSync';

/** Area di lavoro di un'azienda: sezioni nell'URL (#/t/<tenant>/<sezione>/<id>). */
export function TenantShell({
  tenant,
  me,
  route,
  onLogout,
}: {
  tenant: Tenant;
  me: Me;
  route: string[];
  onLogout: () => void;
}) {
  const [section = 'dashboard', id] = route;
  const taskOnly = tenant.access_scope === 'tasks';
  const dataScoped = tenant.access_scope === 'data';
  const [workTab, setWorkTab] = useState<'tasks' | 'dashboard' | 'data' | 'views' | 'dues' | 'executions'>('tasks');
  const isManager = !taskOnly && (tenant.role === 'owner' || tenant.role === 'admin');
  const scope = useQuery({
    queryKey: ['tenant', tenant.id, 'procedure-scope'],
    queryFn: () => api<{ tutte: boolean; aree: string[] }>(`/tenants/${tenant.id}/procedure-scope`),
    enabled: !isManager && !taskOnly,
  });
  // Il livello su cui si lavora (ADR 0210 § 8): tutta l'azienda o una sede, ricordato per azienda in questo browser.
  const [livello, setLivelloState] = useState<string | null>(() => readLevel(tenant.id));
  const setLivello = useCallback(
    (siteId: string | null) => {
      setLivelloState(siteId);
      writeLevel(tenant.id, siteId);
    },
    [tenant.id],
  );
  const value = useMemo<TenantContextValue>(
    () => ({
      tenant,
      me,
      isManager,
      procedureAreas: taskOnly ? [] : isManager || scope.data?.tutte ? 'tutte' : (scope.data?.aree ?? []),
      t: (path: string) => `/tenants/${tenant.id}${path}`,
      livello,
      setLivello,
    }),
    [tenant, me, isManager, taskOnly, scope.data, livello, setLivello],
  );
  const go = (s: string) => navigate(`/t/${tenant.id}/${s}`);
  // Chi rientra dall'ingresso torna in quest'azienda (ADR 0118).
  useEffect(() => rememberTenant(tenant.id), [tenant.id]);
  // Ingresso nell'azienda (#/t/<tenant>) e vecchi collegamenti alla configurazione e alla pagina dei dati: con più sedi
  // prima si sceglie dove lavorare, poi la dashboard (ADR 0212).
  const entry = !route.length || section === 'configurazione' || (section === 'dati' && !id);
  // La Fase 0 non è finita (visura da caricare o da cercare, gestionale da comporre): si torna lì, non in un gestionale
  // vuoto (ADR 0201). Solo per chi la può completare.
  const prep = useQuery({
    queryKey: ['tenant', tenant.id, 'preparation'],
    queryFn: () => api<PreparationState>(`/tenants/${tenant.id}/onboarding/preparation`),
    enabled: isManager,
    staleTime: 30_000,
  });
  useEffect(() => {
    if (isManager && prep.data && !prep.data.pronta && !prep.data.rifiutata) window.location.replace(`#/t/${tenant.id}/preparazione`);
  }, [isManager, prep.data, tenant.id]);

  if (taskOnly || dataScoped) return (
    <TenantContext.Provider value={value}>
      <main className="mx-auto max-w-5xl px-3 py-4 lg:px-6">
        <header className="mb-6 flex flex-wrap items-center gap-3 rounded-xl bg-white p-4 shadow-sm">
          <div className="min-w-0 flex-1">
            <p className="font-semibold">{tenant.name}</p>
            <p className="text-xs text-ink/60">{me.name} · {me.email}</p>
          </div>
          <Button variant="ghost" onClick={onLogout}>Esci</Button>
        </header>
        <nav className="mb-5 flex flex-wrap gap-2"><Button variant={workTab === 'tasks' ? 'primary' : 'ghost'} onClick={() => setWorkTab('tasks')}>I tuoi compiti</Button>{dataScoped && <Button variant={workTab === 'dashboard' ? 'primary' : 'ghost'} onClick={() => setWorkTab('dashboard')}>La tua dashboard</Button>}{dataScoped && <Button variant={workTab === 'data' ? 'primary' : 'ghost'} onClick={() => setWorkTab('data')}>I tuoi dati</Button>}{dataScoped && <Button variant={workTab === 'views' ? 'primary' : 'ghost'} onClick={() => setWorkTab('views')}>Le tue viste</Button>}{dataScoped && <Button variant={workTab === 'dues' ? 'primary' : 'ghost'} onClick={() => setWorkTab('dues')}>Scadenzario</Button>}<Button variant={workTab === 'executions' ? 'primary' : 'ghost'} onClick={() => setWorkTab('executions')}>Esecuzioni e approvazioni</Button></nav>
        {workTab === 'dashboard' && dataScoped ? <MemberDashboardPage /> : workTab === 'data' && dataScoped ? <MyDataPage /> : workTab === 'views' && dataScoped ? <ViewsPage /> : workTab === 'dues' && dataScoped ? <ScadenzarioPage /> : workTab === 'executions' ? <ExecutionsPage /> : <Today />}
      </main>
    </TenantContext.Provider>
  );

  let page;
  if (entry) page = <EntryRedirect />;
  else if (section === 'sede') page = <LevelChooserPage />;
  else if (section === 'dashboard') page = <DashboardPage />;
  else if (section === 'esecuzioni') page = id ? <RunDetailPage runId={id} /> : <Runs />;
  else if (section === 'processi') {
    if (id === 'nuovo') page = <NewProcess />;
    else if (id === 'costruisci')
      page = <BuilderPage key={route[2] ?? 'nuova'} conversationId={route[2]} />;
    else if (id === 'rivedi')
      page = <BuilderPage key={`rivedi-${route[2]}`} processId={route[2]} />;
    else page = id ? <ProcessDetailPage processId={id} /> : <Processes />;
  } else if (section === 'persone')
    page = <People key={id ?? 'elenco'} importing={id === 'importa'} />;
  else if (section === 'archivio') page = <ArchivePage path={route.slice(1)} />;
  else if (section === 'sedi') page = id ? <SitePage key={id} id={id} /> : <SitesPage />;
  else if (section === 'impresa') page = <CompanyPage />;
  else if (section === 'organi') page = <GovernancePage />;
  else if (section === 'ruoli') page = <RolesPage />;
  else if (section === 'sistemi') page = <SystemsPage />;
  else if (section === 'accessi-dati') page = <DataAccessPage />;
  else if (section === 'esecutori') page = <ExecutionsPage />;
  else if (section === 'conti') page = <AccountsPage />;
  else if (section === 'e' && id)
    page = route[2] ? (
      <EntityCardPage key={`${id}/${route[2]}`} typeKey={id} rif={decodeURIComponent(route[2])} />
    ) : (
      <EntityTypePage key={id} typeKey={id} />
    );
  else if (section === 'v' && id) page = <RoleViewPage key={id} ruolo={id} />;
  // «nuova/<tipo>/<legame>/<voce>»: la voce nuova aggiunta dalla scheda del suo genitore (ADR 0238).
  else if (section === 'nuova' && id)
    page = <NewEntityPage key={route.join('/')} typeKey={id} preset={route[2] && route[3] ? { [decodeURIComponent(route[2])]: decodeURIComponent(route[3]) } : {}} />;
  else if (section === 'dati')
    page =
      id === 'salvati' ? (
        <StoredDataPage typeKey={route[2]} />
      ) : id === 'scheda' && route[2] && route[3] ? (
        <EntityCardPage key={`${route[2]}/${route[3]}`} typeKey={route[2]} rif={decodeURIComponent(route[3])} />
      ) : (
        <DashboardPage />
      );
  else if (section === 'procedure') page = <ProceduresPage />;
  else if (section === 'raccolta') page = <CollectionPage key={id ?? 'consigliato'} step={id} />;
  else if (section === 'software')
    page =
      id === 'scheda' && route[2] ? (
        <SoftwareDetailPage key={route[2]} id={route[2]} />
      ) : (
      <SoftwarePage
        key={route.join('/')}
        mail={id === 'posta'}
        outcome={
          id === 'esito' && route[2]
            ? { ok: route[2] === 'ok', message: decodeURIComponent(route[3] ?? '') }
            : undefined
        }
      />
      );
  else if (section === 'collega') page = <ConnectPage kind={id} target={route[2]} />;
  else if (section === 'impostazioni') page = <SettingsPage me={me} section={id} base={`/t/${tenant.id}/impostazioni`} />;
  else if (section === 'controlli') page = <ControlsPage />;
  else if (section === 'indicatori') page = <KpisPage />;
  else if (section === 'viste') page = <ViewsPage key={id ?? ''} initialId={id} />;
  else if (section === 'scadenzario') page = <ScadenzarioPage />;
  else if (section === 'proposte') page = <Proposals />;
  else if (section === 'oggi') page = <Today />;
  else page = <DashboardPage />;

  // Voce evidenziata nel menu: il tipo anche dalla sua scheda (ADR 0112).
  const active =
    section === 'e' || section === 'nuova' || section === 'v' ? `${section === 'v' ? 'v' : 'e'}/${id}` : section === 'dati' && id === 'scheda' ? `e/${route[2]}` : section === 'dati' && id === 'salvati' ? 'dati/salvati' : section;

  return (
    <TenantContext.Provider value={value}>
      {value.isManager && <ConditionAnswersSync />}
      <ShellLayout active={active} onLogout={onLogout}>
        {value.isManager && <WorkBand onOpen={() => go('raccolta')} />}
        {page}
      </ShellLayout>
    </TenantContext.Provider>
  );
}

/** L'ingresso nell'azienda (ADR 0212): con più sedi la scelta di dove lavorare, altrimenti la dashboard. */
function EntryRedirect() {
  const { tenant } = useTenant();
  const scopes = useTenantQuery<ModelScopesDto>(['model-scopes'], '/model/scopes');
  const levels = useLevels();
  const ready = !!scopes.data || scopes.isError;
  useEffect(() => {
    if (ready) window.location.replace(`#/t/${tenant.id}/${levels.multi ? 'sede' : 'dashboard'}`);
  }, [ready, levels.multi, tenant.id]);
  return <SectionLoader />;
}

/** Menu laterale fisso sullo schermo grande; sul telefono si apre con il pulsante e si chiude cambiando pagina. */
function ShellLayout({ active, onLogout, children }: { active: string; onLogout: () => void; children: ReactNode }) {
  const { tenant, me } = useTenant();
  // Colori e logo dell'azienda (ADR 0117); dentro una sede i colori della sede (ADR 0210 § 3).
  const levels = useLevels();
  const { logo } = useBrand(tenant.id, true, levels.site?.marchio?.colori);
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [active]);
  return (
    <div className="lg:flex">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 bg-white shadow-[0_0_24px_rgba(89,102,122,0.08)] lg:block">
        <Sidebar active={active} logo={logo} />
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button className="absolute inset-0 bg-ink/30" aria-label="Chiudi il menu" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-xl">
            <Sidebar active={active} logo={logo} />
          </aside>
        </div>
      )}
      <main className="min-w-0 flex-1 px-3 pb-6 pt-3 lg:px-6 lg:pt-4">
        {/* Barra in alto (ADR 0118): l'azienda, che si cambia da qui, e l'utente con le impostazioni. */}
        <TopBar
          me={me}
          current={tenant}
          onLogout={onLogout}
          extra={<LevelSwitch />}
          start={
            <button className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-accent hover:bg-accent/10 lg:hidden" aria-label="Apri il menu" onClick={() => setOpen(true)}>
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
          }
        />
        <div className="min-w-0">{children}</div>
      </main>
    </div>
  );
}

/**
 * Fascia della coda di lavorazione (ADR 0043): finché il sistema sta leggendo (posta, file, programmi) dice quanto
 * manca, con la barra; sparisce da sola quando ha finito.
 */
function WorkBand({ onOpen }: { onOpen: () => void }) {
  const work = useWork();
  const a = work.data?.active;
  if (!a || !a.sources) return null;
  return (
    <button type="button" onClick={onOpen} className="mb-5 block w-full rounded-lg border border-black/10 bg-white px-4 py-2 text-left text-sm shadow-sm hover:bg-black/[0.02]">
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Sto leggendo i vostri dati</span>
        <span className="text-ink/60">
          {a.sources === 1 ? '1 fonte' : `${a.sources} fonti`} · {a.done.toLocaleString('it-IT')} di {a.items.toLocaleString('it-IT')} elementi
          {work.data?.sources.some((s) => s.paused) ? ' · in pausa: apri per vedere perché' : a.eta_seconds !== null ? ` · ${etaText(a.eta_seconds)} alla fine` : ' · calcolo il tempo che manca…'}
        </span>
        <span className="flex-1" />
        <span className="text-xs text-ink/50">{a.percent}%</span>
      </span>
      <span className="mt-1.5 block">
        <Bar percent={a.percent} />
      </span>
    </button>
  );
}
